import type { DecodedZone } from "./types";

// Decodes moment-timezone's packed format: base60 numerals, deltas from the previous entry.

const charCodeToInt = (charCode: number) => {
	if (charCode > 96) {
		return charCode - 87;
	}
	if (charCode > 64) {
		return charCode - 29;
	}
	return charCode - 48;
};

const unpackBase60 = (value: string) => {
	let index = 0;
	const parts = value.split(".");
	const whole = parts[0]!;
	const fractional = parts[1] || "";
	let multiplier = 1;
	let output = 0;
	let sign = 1;

	if (value.charCodeAt(0) === 45) {
		index = 1;
		sign = -1;
	}
	for (; index < whole.length; index++) {
		const number = charCodeToInt(whole.charCodeAt(index));
		output = 60 * output + number;
	}
	for (index = 0; index < fractional.length; index++) {
		multiplier /= 60;
		const number = charCodeToInt(fractional.charCodeAt(index));
		output += number * multiplier;
	}
	return output * sign;
};

const arrayToInt = (array: string[]) => {
	const output = new Array<number>(array.length);
	for (let index = 0; index < array.length; index++) {
		output[index] = unpackBase60(array[index]!);
	}
	return output;
};

const intToUntil = (array: number[], length: number) => {
	for (let index = 0; index < length; index++) {
		array[index] = Math.round((array[index - 1] || 0) + array[index]! * 60_000);
	}
	array[length - 1] = Number.POSITIVE_INFINITY;
};

const mapIndices = <Value>(source: Value[], indices: number[]) => {
	const output = new Array<Value>(indices.length);
	for (let index = 0; index < indices.length; index++) {
		output[index] = source[indices[index]!]!;
	}
	return output;
};

const closest = (number: number, array: number[]) => {
	const length = array.length;
	if (number < array[0]!) {
		return 0;
	}
	if (length > 1 && array[length - 1] === Number.POSITIVE_INFINITY && number >= array[length - 2]!) {
		return length - 1;
	}
	if (number >= array[length - 1]!) {
		return -1;
	}

	let low = 0;
	let high = length - 1;
	while (high - low > 1) {
		const middle = Math.floor((low + high) / 2);
		if (array[middle]! <= number) {
			low = middle;
		} else {
			high = middle;
		}
	}
	return high;
};

export const unpack = (packed: string): DecodedZone => {
	const data = packed.split("|");
	const offsets = arrayToInt(data[2]!.split(" "));
	const indices = arrayToInt(data[3]!.split(""));
	const untils = arrayToInt(data[4]!.split(" "));

	intToUntil(untils, indices.length);

	return {
		name: data[0]!,
		abbrs: mapIndices(data[1]!.split(" "), indices),
		offsets: mapIndices(offsets, indices),
		untils,
		population: Number.parseInt(data[5]!, 10) || 0,
		utcOffset(timestampMs) {
			return this.offsets[closest(timestampMs, this.untils)]!;
		},
	};
};
